import { FormControl, FormGroup } from '@angular/forms';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    TradingPlanService,
    TradingPlanResponseModel,
    TradingPlanUpdateStatusRequestModel
} from '@spa/api';

import { RequiredValidatorKey } from '@spa/validators';
import { TradingPlanStatusControlOptions, TradingPlanStatusesControl } from 'appAssets/descriptors';
import { Helper, RadioGroupControl } from 'appShared/classes';
import { RadioGroupOptionInterface } from 'appShared/components';

export class UpdatePlanStatusesForm {
    private tradingPlanService: TradingPlanService;

    public formGroup: FormGroup;
    public control: RadioGroupControl;

    constructor(tradingPlanService: TradingPlanService) {
        this.tradingPlanService = tradingPlanService;
    }

    private mapStatusControlOptions(
        plan: TradingPlanResponseModel,
        triggers: number[],
        currentTrigger: number,
        options: RadioGroupOptionInterface[],
        disabledOption: boolean
    ): RadioGroupOptionInterface[] {
        return options.map(option => {
            const trigger = Helper.enumsHelper()
                .getTradingPlanStatusTriggerByStatus(option.value as number);

            if ((triggers.concat(currentTrigger).indexOf(trigger) < 0) || disabledOption) {
                option.disabled = true;
            }

            if (option.value === plan.tradingPlanStatusEnum) {
                option.checked = true;

                this.control.value = plan.tradingPlanStatusEnum;
            }

            return option;
        });
    }

    initForm(plan: TradingPlanResponseModel, planRequestTriggers: number[], disabledControlItems: boolean): void {
        this.formGroup = new FormGroup({});
        this.control = TradingPlanStatusesControl();

        const currentTrigger = Helper.enumsHelper()
            .getTradingPlanStatusTriggerByStatus(plan.tradingPlanStatusEnum);

        let options = TradingPlanStatusControlOptions();

        
        this.control.options = this.mapStatusControlOptions(
            plan,
            planRequestTriggers,
            currentTrigger,
            options,
            disabledControlItems
        );

        this.formGroup.addControl(this.control.name, new FormControl(this.control.value, this.control.validators));
    }

    refreshForm(plan: TradingPlanResponseModel, triggers: number[], disabledControlItems: boolean): void {
        const currentTrigger = Helper.enumsHelper()
            .getTradingPlanStatusTriggerByStatus(plan.tradingPlanStatusEnum);

        this.control.options.forEach(option => {
            const trigger = Helper.enumsHelper()
                .getTradingPlanStatusTriggerByStatus(option.value as number);

            if ((triggers.concat(currentTrigger).indexOf(trigger) < 0) || disabledControlItems) {
                option.disabled = true;
            }
            else {
                option.disabled = false;
            }

            if (option.value === plan.tradingPlanStatusEnum) {
                option.checked = true;

                this.control.value = plan.tradingPlanStatusEnum;
            }
        });
    }

    validateForm(messagesFunction: (controlLabel?: string) => { [key: string]: string }): boolean {
        if (this.formGroup.invalid) {
            const control = this.formGroup.controls[this.control.name];

            this.control.errors = Object.keys(control.errors).map((key: string) => {
                if (key === RequiredValidatorKey) {
                    return 'Different status must be selected.';
                }

                return messagesFunction(this.control.placeholder)[key];
            });

            return false;
        }

        return true;
    }

    createRequest(
        plan: TradingPlanResponseModel
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        const status = this.formGroup.value[this.control.name]; 

        if (plan.tradingPlanStatusEnum !== status) {
            const model = new TradingPlanUpdateStatusRequestModel(
                plan.id,
                Helper.enumsHelper().getTradingPlanStatusTriggerByStatus(status)
            );

            return this.tradingPlanService.updateStatus(model.serialize())
                .pipe(
                    map((response: ApiResponse<ApiError[] | null>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse(
                                response 
                            );
                        }
                        plan.tradingPlanStatusEnum = status;                       

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
